import { Add, Delete, DoNotDisturb, DragIndicator, Edit } from '@mui/icons-material';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DragDropContext, Draggable, DraggableProvided, DraggableStateSnapshot, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { TemplateField } from '../../../types/FieldCommon';
import { UserSettingsCollapseKeys } from '../../../types/User';
import { Loading } from '../../../uikit';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../../AccordionSection';
import ConfirmationModal from '../../ConfirmationModal';
import FieldOptionsCell from './FieldOptionsCell';
import TemplateFieldModal from './TemplateFieldModal';
import { formatFieldValue, isSelectionType } from '../../../utility/fieldUtils';
import { FormFieldMetadata, getFieldTypeLabel } from './templateFieldUtils';

interface ITemplateFieldsSectionProps<TField extends TemplateField> {
  fields: TField[];
  setFields: (fields: TField[]) => void;
  collapseId: UserSettingsCollapseKeys;
  createField?: () => TField;
  isForm?: boolean;
}

export default function TemplateFieldsSection<TField extends TemplateField>({
  collapseId,
  fields,
  setFields,
  createField,
  isForm = false,
}: ITemplateFieldsSectionProps<TField>) {
  const [modalOpen, setModalOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState<TField | null>(null);
  const [uuidToDelete, setUuidToDelete] = useState<string | null>(null);

  const { disabled } = useContext(DisableContext);

  if (!fields) return <Loading />;

  const openAddModal = () => {
    setFieldToEdit(null);
    setModalOpen(true);
  };

  const openEditModal = (field: TField) => {
    setFieldToEdit(field);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFieldToEdit(null);
  };

  const handleSaveField = (field: TField) => {
    const exists = _.some(fields, (existing) => existing.uuid === field.uuid);
    setFields(exists ? _.map(fields, (existing) => (existing.uuid === field.uuid ? field : existing)) : [...fields, field]);
  };

  const handleDeleteField = () => {
    if (!uuidToDelete) return;

    setFields(_.reject(fields, (field) => field.uuid === uuidToDelete));
    setUuidToDelete(null);
  };

  /**
   * Reorders the fields in the template's fields array after a drag-and-drop operation.
   * @param list The array of fields to reorder.
   * @param startIndex The index of the field to move.
   * @param endIndex The destination index.
   * @returns The reordered array of fields.
   */
  const reorder = (list: TField[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    setFields(reorder(fields, result.source.index, result.destination.index));
  };

  return (
    <AccordionSection
      title="Custom Fields"
      headerElements={
        <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={openAddModal} disabled={disabled}>
          Add
        </ResponsiveButton>
      }
      collapseId={collapseId}
      hasData={!_.isEmpty(fields)}
      scrollable
    >
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Field Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Default Value</TableCell>
              {isForm && <TableCell sx={{ width: 96 }}>Required</TableCell>}
              <TableCell sx={{ width: 112 }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="fields-droppable">
              {(droppableProvided: DroppableProvided) => (
                <TableBody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                  {_.map(fields, (field: TField, index: number) => (
                    <Draggable key={field.uuid} draggableId={field.uuid} index={index} isDragDisabled={disabled}>
                      {(draggableProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                        <TableRow
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          sx={snapshot.isDragging ? { display: 'table', bgcolor: 'background.paper' } : undefined}
                        >
                          <TableCell sx={{ width: 48, cursor: disabled ? 'default' : 'grab' }} {...draggableProvided.dragHandleProps}>
                            {disabled ? <DoNotDisturb color="disabled" /> : <DragIndicator color="action" />}
                          </TableCell>
                          <TableCell>{field.label}</TableCell>
                          <TableCell>{getFieldTypeLabel(field.type)}</TableCell>
                          <TableCell>{isSelectionType(field.type) ? <FieldOptionsCell options={field.options ?? []} /> : '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {formatFieldValue(field)}
                          </TableCell>
                          {isForm && <TableCell>{(field as TField & FormFieldMetadata).required ? 'Yes' : 'No'}</TableCell>}
                          <TableCell sx={{ width: 112 }}>
                            <IconButton onClick={() => openEditModal(field)} disabled={disabled} size="small">
                              <Edit />
                            </IconButton>
                            <IconButton onClick={() => setUuidToDelete(field.uuid)} disabled={disabled} size="small">
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
      </TableContainer>

      <TemplateFieldModal<TField>
        isOpen={modalOpen}
        onCancel={closeModal}
        onSave={handleSaveField}
        fieldToEdit={fieldToEdit}
        isForm={isForm}
        createField={createField}
      />

      <ConfirmationModal text="Remove this field?" isOpen={uuidToDelete !== null} onConfirm={handleDeleteField} onCancel={() => setUuidToDelete(null)} />
    </AccordionSection>
  );
}
